"use client";

import { useState } from "react";
import { COMMON } from "@/lib/test/test-ids";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Plus, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useUser } from "@/components/providers/UserProvider";
import { Role, navigationConfig } from "@/components/navigation/navigation";
import { ThemeSwitcher } from "../theme-switcher";
import UserDropdown from "../UserDropdown";
import { LanguageSelector } from "../language-selector";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, profileLoading } = useUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { t } = useTranslation(['common', 'doctor-platform', 'translation']);

  // Wait for profile to load to get the correct role
  const userRole = profileLoading ? "user" : ((profile?.role as Role) || "user");
  
  if (import.meta.env.DEV) {
    console.log('[Navbar] User role:', { 
      profileRole: profile?.role, 
      defaultedTo: userRole,
      profileLoading,
      hasProfile: !!profile,
      userId: user?.id,
      profileData: profile
    });
  }
  
  const authorizedRoutes = navigationConfig.mainNav.filter((route) =>
    route.roles.includes(userRole)
  );

  return (
    <>
      {/* Fixed header */}
      <div className="fixed inset-0 flex flex-col w-full pointer-events-none z-50">
        <header className="pointer-events-auto z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 h-14" data-testid={COMMON.NAVBAR.CONTAINER}>
          <div className="container flex h-14 items-center">
            <div className="flex items-center flex-1">
              <Button
                variant="ghost"
                className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                data-testid={COMMON.NAVBAR.MENU_BUTTON}
              >
                {isSidebarOpen ? (
                  <X className="h-5 w-5" title={t('collapseSidebar')} />
                ) : (
                  <Menu className="h-5 w-5" title={t('openSidebar')} />
                )}
              </Button>
              <Link to="/dashboard" className="flex items-center space-x-2" data-testid={COMMON.NAVBAR.LOGO}>
                <img
                  src="/SHS_Logo_V3.png"
                  alt={t('app_logoAlt', 'SHS Logo')}
                  className="h-8 w-auto"
                />
                <span className="font-bold hidden sm:inline">
                  {t('app_title', 'Sahelian Health Solutions')}
                </span>
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <UserDropdown data-testid={COMMON.NAVBAR.USER_DROPDOWN} />
            </div>
          </div>
        </header>
      </div>

      {/* Sidebar */}
      <div
        className={cn(
          "fixed left-0 top-14 z-40 h-[calc(100vh-3.5rem)] w-64 border-r bg-background transition-transform lg:translate-x-0",
          "flex flex-col overflow-y-auto",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid={COMMON.SIDEBAR.CONTAINER}
      >
        {/* Quick Action */}
        <div className="p-4 border-b">
          <Button
            onClick={() => navigate('/prescriptions/create')}
            className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
            size="lg"
          >
            <Plus className="mr-2 h-5 w-5" />
            <span className="font-semibold">{t('doctor-platform:navbar.newPrescription')}</span>
            <Sparkles className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 space-y-1 p-4">
          {authorizedRoutes.map((route) => {
            const isActive = location.pathname === route.href;
            return (
              <Link
                key={route.href}
                to={route.href}
                onClick={() => setIsSidebarOpen(false)}
                className={cn(
                  "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:bg-secondary/50"
                )}
                data-testid={COMMON.SIDEBAR.NAV_ITEM(route.label)}
              >
                {route.icon && <route.icon className="h-5 w-5" />}
                <span>{t(route.label, { ns: 'common' })}</span>
              </Link>
            );
          })}
        </nav>

        {/* Settings Section */}
        <div className="border-t p-4 space-y-4">
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-muted-foreground">{t('common.theme')}</label>
            <ThemeSwitcher data-testid={COMMON.SIDEBAR.THEME_SWITCHER} />
          </div>
          
          <div className="flex flex-col space-y-2">
            <LanguageSelector className="w-full" />
          </div>
        </div>
      </div>

      {/* Backdrop for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
}
